import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstButton from "../../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import RequiredLabel from "../../../../../components/RequiredLabel/RequiredLabel";
import translate from "../../../../../services/Translate";
import { fetchInstances, updateExercise } from "./EditExerciseModal.function";

const EditExerciseModal = ({ show, onClose, exercise, language, onSuccess }: any) => {
  const [isEditing, setIsEditing] = useState(false);
  const [instances, setInstances] = useState<any[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [manualInstanceType, setManualInstanceType] = useState("");

  const [ownerLabel, setOwnerLabel] = useState<string>("");
  const [moduleLabel, setModuleLabel] = useState<string>("");

  const _typeItems = [
    { id: "html", name: "HTML" },
    { id: "ipynb", name: "IPYNB" },
    { id: "raven360", name: "RAVEN360" },
    { id: "moodle", name: "MOODLE" },
  ];

  const _instanceProviderItems = [
    { id: "aws", name: "AWS" },
    { id: "proxmox", name: "Proxmox" },
    { id: "none", name: translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.VALUES.NONE") },
  ];

  const _languageItems = [
    { id: "fr-FR", name: "fr-FR" },
    { id: "en-US", name: "en-US" },
  ];

  const [form, setForm] = useState({
    name: "",
    description: "",
    filename: "",
    language: "fr-FR",
    difficulty: 5,
    duration: 60,
    owner_id: null as number | null,
    type: "ipynb",
    instance_type_id: null as string | null,
    instance_provider: "aws" as string | null,
    is_deprecated: false,
    origin_module_id: null as number | null,
  });

  useEffect(() => {
    if (form.type === "html") {
      fetchInstances(setInstances, setLoadingInstances, language);
    }
  }, [form.type, language]);

  useEffect(() => {
    setForm({
      name: exercise.name ?? "",
      description: exercise.description ?? "",
      filename: exercise.filename ?? "",
      language: exercise.language ?? "fr-FR",
      difficulty: exercise.difficulty ?? 5,
      duration: exercise.duration ?? 60,
      owner_id: exercise.owner?.id ?? null,
      type: (exercise.type ?? "ipynb") as string,
      instance_type_id: exercise.instance_type ?? null,
      instance_provider:
        exercise.type === "html" ? exercise.instance_provider ?? (exercise.instance_type ? "aws" : "none") : null,
      is_deprecated: !!exercise.is_deprecated,
      origin_module_id: exercise.origin_module?.id ?? null,
    });

    setOwnerLabel(exercise.owner?.full_name ?? "");
    setModuleLabel(exercise.origin_module?.name ?? "");
    setManualInstanceType(
      exercise.instance_provider === "proxmox" && exercise.instance_type !== null ? String(exercise.instance_type) : ""
    );
  }, [exercise]);

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _handleSubmit = () => {
    const hasInstance = form.type === "html" && form.instance_provider && form.instance_provider !== "none";
    const payload = {
      ...form,
      instance_provider: hasInstance ? form.instance_provider : null,
      instance_type_id: hasInstance
        ? form.instance_provider === "aws"
          ? form.instance_type_id
          : manualInstanceType.trim()
        : null,
    };

    updateExercise(
      exercise.id,
      payload,
      setIsEditing,
      () => {
        onSuccess?.();
        onClose?.();
      },
      language
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.TITLE")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
            <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "GLOBAL.DESCRIPTION")} />
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(event) => _handleChange("description", event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.FILENAME")} />
            <Form.Control value={form.filename} onChange={(event) => _handleChange("filename", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
            <MultiSelectDropdown
              items={_languageItems}
              language={language}
              label={_languageItems.find((language) => language.id === form.language)!.name}
              isSingleSelect
              badgeItemProperty="name"
              displayItem={(item: any) => <span>{item.name}</span>}
              onConfirm={([item]: any[]) => _handleChange("language", item.id)}
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.DIFFICULTY")} />
            <Form.Control
              type="number"
              min={1}
              max={10}
              value={form.difficulty}
              onChange={(event) => _handleChange("difficulty", Number(event.target.value))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "GLOBAL.DURATION")} />
            <Form.Control
              type="number"
              min={1}
              value={form.duration}
              onChange={(event) => _handleChange("duration", Number(event.target.value))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "GLOBAL.TYPE")} />
            <MultiSelectDropdown
              items={_typeItems}
              language={language}
              label={_typeItems.find((type) => type.id === form.type)!.name}
              isSingleSelect
              badgeItemProperty="name"
              displayItem={(item: any) => <span>{item.name}</span>}
              onConfirm={([item]: any[]) =>
                setForm((prev) => ({
                  ...prev,
                  type: item.id,
                  instance_type_id: item.id === "html" ? prev.instance_type_id : null,
                  instance_provider: item.id === "html" ? prev.instance_provider : null,
                }))
              }
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>

          {form.type === "html" && (
            <Form.Group className="mb-3">
              <RequiredLabel
                label={translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.INSTANCE_PROVIDER")}
              />
              <MultiSelectDropdown
                items={_instanceProviderItems}
                language={language}
                label={
                  _instanceProviderItems.find((provider) => provider.id === form.instance_provider)?.name ||
                  translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.PLACEHOLDERS.SELECT_INSTANCE_PROVIDER")
                }
                isSingleSelect
                badgeItemProperty="name"
                displayItem={(item: any) => <span>{item.name}</span>}
                onConfirm={([item]: any[]) => {
                  setForm((prev) => ({
                    ...prev,
                    instance_provider: item.id,
                    instance_type_id: null,
                  }));
                  setManualInstanceType("");
                }}
                borderToggle
                btnWidth="100%"
                toggleClass="w-100"
              />
            </Form.Group>
          )}

          {form.type === "html" && form.instance_provider && form.instance_provider !== "none" && (
            <Form.Group className="mb-3">
              <RequiredLabel
                label={translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.INSTANCE_TYPE")}
              />
              {form.instance_provider === "aws" ? (
                <MultiSelectDropdown
                  items={instances}
                  language={language}
                  label={
                    instances.find((instance) => instance.id === form.instance_type_id)?.proper_name ||
                    translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.PLACEHOLDERS.SELECT_INSTANCE_TYPE")
                  }
                  isSingleSelect
                  badgeItemProperty="proper_name"
                  displayItem={(item: any) => <span>{item.proper_name}</span>}
                  onConfirm={([item]: any[]) => _handleChange("instance_type_id", item ? item.id : null)}
                  borderToggle
                  btnWidth="100%"
                  toggleClass="w-100"
                  disabled={loadingInstances}
                  isSingleSelectRemovable
                  defaultSelectedItems={instances.filter((instance) => instance.id === form.instance_type_id)}
                />
              ) : (
                <Form.Control
                  type="number"
                  value={manualInstanceType}
                  onChange={(event) => setManualInstanceType(event.target.value)}
                />
              )}
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.OWNER")} />
            <MultiSelectLiveDropdown
              category={["users"]}
              language={language}
              isSingleSelect
              toggleClass={`w-100 border ${!form.owner_id ? "text-muted" : ""}`}
              label={ownerLabel || translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.PLACEHOLDERS.SELECT_OWNER")}
              initialSelectedItems={[
                {
                  value: {
                    ...exercise.owner,
                    first_name: exercise.owner.full_name.split(" ")[0],
                    last_name: exercise.owner.full_name.split(" ").slice(1).join(" "),
                  },
                  category: "users",
                },
              ]}
              onConfirm={(selected) => {
                const user = selected[0].value;
                setOwnerLabel(`${user.first_name} ${user.last_name}`);
                setForm((prev) => ({ ...prev, owner_id: user.id }));
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.ORIGIN_MODULE")}</Form.Label>
            <MultiSelectLiveDropdown
              category={["modules"]}
              language={language}
              isSingleSelect
              toggleClass={`w-100 border ${!form.origin_module_id ? "text-muted" : ""}`}
              label={
                moduleLabel || translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.PLACEHOLDERS.SELECT_ORIGIN_MODULE")
              }
              initialSelectedItems={
                exercise.origin_module ? [{ value: exercise.origin_module, category: "modules" }] : []
              }
              onConfirm={(selected) => {
                const module = selected?.[0]?.value ?? null;
                setModuleLabel(module ? module.name : "");
                setForm((prev) => ({ ...prev, origin_module_id: module?.id ?? null }));
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="exercise-deprecated"
              label={translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.IS_DEPRECATED")}
              checked={form.is_deprecated}
              onChange={(event) => _handleChange("is_deprecated", event.target.checked)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <DstButton variant="secondary" value={translate(language, "GLOBAL.CANCEL")} clickFunction={onClose} />
        <DstButton
          loading={isEditing}
          value={translate(language, "GLOBAL.SAVE")}
          clickFunction={_handleSubmit}
          disabled={
            isEditing ||
            loadingInstances ||
            (form.type === "html" &&
              (!form.instance_provider ||
                (form.instance_provider === "aws"
                  ? form.instance_type_id == null
                  : form.instance_provider !== "none" && !manualInstanceType.trim()))) ||
            !form.name.trim() ||
            !form.filename.trim() ||
            !form.language ||
            !form.type ||
            !form.difficulty ||
            !form.duration ||
            !form.description.trim()
          }
        />
      </Modal.Footer>
    </Modal>
  );
};

export default EditExerciseModal;
